import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { FiMessageSquare, FiPaperclip, FiTrash2, FiUpload } from 'react-icons/fi';
import { uploadAttachment, deleteAttachment, fetchAttachmentBlob } from '../services/api';

const STATUT_LABELS = { KO: 'Echoue (KO)', BL: 'Bloque (BL)' };

function AttachmentImage({ projectId, testCaseId, attachment }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let revoked = false;
    if (projectId && testCaseId && attachment?.id) {
      fetchAttachmentBlob(projectId, testCaseId, attachment.id).then((url) => {
        if (!revoked) setSrc(url);
      }).catch(() => {});
    }
    return () => {
      revoked = true;
      if (src) URL.revokeObjectURL(src);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, testCaseId, attachment?.id]);

  if (!src) return <div className="attachment-thumb-placeholder" />;
  return <img src={src} alt={attachment.filename} className="attachment-thumb" />;
}

export default function CommentModal({
  show, onClose, onSubmit, initialComment, statut, loading,
  projectId, testCaseId, attachments = [], onAttachmentChange,
}) {
  const [comment, setComment] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (show) {
      setComment(initialComment || '');
      setPendingFiles([]);
    }
  }, [show, initialComment]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 10 * 1024 * 1024;
    const valid = files.filter(f => {
      if (f.size > maxSize) { alert(`${f.name} depasse 10 Mo.`); return false; }
      return true;
    });
    setPendingFiles(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExisting = async (attId) => {
    if (!projectId || !testCaseId) return;
    try {
      await deleteAttachment(projectId, testCaseId, attId);
      if (onAttachmentChange) onAttachmentChange();
    } catch { /* handled by interceptor */ }
  };

  const handleSubmit = async () => {
    setUploading(true);
    // Upload des fichiers en attente
    if (projectId && testCaseId && pendingFiles.length > 0) {
      for (const file of pendingFiles) {
        try {
          await uploadAttachment(projectId, testCaseId, file);
        } catch { /* handled by interceptor */ }
      }
      if (onAttachmentChange) onAttachmentChange();
    }
    setPendingFiles([]);
    setUploading(false);
    onSubmit(comment);
  };

  const handleSkip = () => onSubmit('');

  const isLoading = loading || uploading;

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={`Commentaire - ${STATUT_LABELS[statut] || statut}`}
      icon={<FiMessageSquare />}
      size="md"
      footer={
        <div className="d-flex gap-2 justify-content-end w-100">
          <button className="btn btn-outline-secondary btn-sm" onClick={handleSkip} disabled={isLoading}>
            Passer
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <><span className="spinner-border spinner-border-sm me-1"></span>Enregistrement...</> : 'Enregistrer'}
          </button>
        </div>
      }
    >
      <div>
        <label className="form-label small fw-500" htmlFor="field-observation">
          Observation / Cause de l'echec
        </label>
        <textarea
          id="field-observation"
          className="form-control form-control-sm"
          rows="4"
          placeholder="Decrivez la cause de l'echec, les logs, les observations..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          autoFocus
        />
        <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
          Optionnel : cliquez "Passer" pour changer le statut sans commentaire.
        </div>

        {/* Zone pieces jointes */}
        {projectId && testCaseId && (
          <div className="mt-3">
            <span className="form-label small fw-500 d-flex align-items-center gap-1">
              <FiPaperclip /> Pieces jointes
            </span>

            {/* Pieces jointes existantes */}
            {attachments.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-2">
                {attachments.map(att => (
                  <div key={att.id} className="attachment-item">
                    <AttachmentImage projectId={projectId} testCaseId={testCaseId} attachment={att} />
                    <button
                      className="attachment-delete-btn"
                      onClick={() => handleDeleteExisting(att.id)}
                      title="Supprimer"
                    >
                      <FiTrash2 />
                    </button>
                    <div className="attachment-name">{att.filename}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Fichiers en attente */}
            {pendingFiles.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-2">
                {pendingFiles.map((file, i) => (
                  <div key={i} className="attachment-item attachment-pending">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="attachment-thumb"
                      onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                    />
                    <button
                      className="attachment-delete-btn"
                      onClick={() => removePendingFile(i)}
                      title="Retirer"
                    >
                      <FiTrash2 />
                    </button>
                    <div className="attachment-name">{file.name}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Input file */}
            <label className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
              <FiUpload /> Ajouter une capture
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/bmp"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </label>
            <div className="text-muted mt-1" style={{ fontSize: '0.68rem' }}>
              Images uniquement (PNG, JPEG, GIF, WebP, BMP). Max 10 Mo par fichier.
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
